import type { AccountInfo } from '@azure/msal-common';
import { type Ref, type ComputedRef, computed } from 'vue';
import {
    resolveAccountUnref,
    type MaybeAccountRef,
} from '../utils/accountSelectors';
import { useMsalStore } from '../stores/msal-store';
import type { PublicClientApplication } from '@azure/msal-browser';

export interface UseAccountResult {
    /**
     * The Account-info object.
     */
    accountInfo: Ref<AccountInfo | null>;

    idTokenClaims: ComputedRef<Record<string, any>>;
    /**
     * The name of the account.
     */
    fullName: ComputedRef<string | null>;

    givenName: ComputedRef<string | null>;

    familyName: ComputedRef<string | null>;

    email: ComputedRef<string | null>;

    identityProvider: ComputedRef<string | null>;

    trustFrameworkPolicy: ComputedRef<string | null>;

    tenantId: ComputedRef<string | null>;

    subjectId: ComputedRef<string | null>;

    environment: ComputedRef<string | null>;
}

/**
 * Get the data of the currently active account.
 */
export function useAccount(account?: MaybeAccountRef): UseAccountResult {
    const store = useMsalStore();

    const accountInfo = computed(() =>
        resolveAccountUnref(
            store.msal as PublicClientApplication | null,
            account
        )
    );

    const idTokenClaims = computed<Record<string, any>>(
        () => accountInfo.value?.idTokenClaims ?? {}
    );

    const givenName = computed(() => idTokenClaims.value.given_name ?? null);

    const familyName = computed(() => idTokenClaims.value.family_name ?? null);

    const fullName = computed(() => {
        if (accountInfo.value?.name) return accountInfo.value.name;
        if (givenName.value && familyName.value)
            return `${givenName.value} ${familyName.value}`;
        if (givenName.value) return givenName.value;
        if (familyName.value) return familyName.value;
        return null;
    });

    const email = computed(() => idTokenClaims.value.email ?? null);

    const identityProvider = computed(() => idTokenClaims.value.idp ?? null);

    const trustFrameworkPolicy = computed(
        () => idTokenClaims.value.tfp ?? null
    );

    const tenantId = computed(() => idTokenClaims.value.tenantId ?? null);

    const subjectId = computed(() => idTokenClaims.value.subjectId ?? null);

    const environment = computed(() => accountInfo.value?.environment ?? null);

    return {
        accountInfo,
        fullName,
        idTokenClaims,
        givenName,
        familyName,
        email,
        identityProvider,
        trustFrameworkPolicy,
        tenantId,
        subjectId,
        environment,
    };
}

export function useAccountWhere(
    predicate: (accountInfo: AccountInfo) => boolean
): UseAccountResult {
    return useAccount((accounts) => accounts.filter(predicate)[0]);
}
